import ConfirmModal from '@/components/confirm-modal'
import {
    BADGE_RARITY_COLORS,
    BADGE_RARITY_LABELS,
    BadgesResponse,
    cancelGoal,
    createGoal,
    CreateGoalData,
    getBadges,
    getGamificationProfile,
    getGoals,
    getPointHistory,
    getRankings,
    GOAL_PERIOD_LABELS, GOAL_SCOPE_LABELS,
    GOAL_STATUS_COLORS,
    GOAL_STATUS_LABELS,
    GOAL_TYPE_LABELS, GOAL_TYPE_UNITS,
    GoalFilters,
    RANKING_PERIOD_LABELS,
    RankingPeriod,
    SalesBadgeDefinition,
    SalesGoal,
    SalesGoalPeriod, SalesGoalScope, SalesGoalStatus,
    SalesGoalType,
    SalesPointLog,
    SalesRanking,
    UserGamificationProfile
} from '@/lib/api/sales/gamification-api'
import { getWallets, SalesWallet } from '@/lib/api/sales/wallets-api'
import { getUsersByCompany } from '@/lib/api/users/users-api'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import { useAuth } from '@/store/hooks'
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
    MdAccessTime,
    MdAdd,
    MdArrowDownward,
    MdArrowUpward,
    MdCancel,
    MdClose,
    MdEmojiEvents,
    MdFlag,
    MdHistory,
    MdLeaderboard,
    MdLock,
    MdMilitaryTech,
    MdPerson,
    MdRemove
} from 'react-icons/md'
import { toast } from 'react-toastify'

// ── Types ────────────────────────────────────────────────────────────────────

type Tab = 'profile' | 'rankings' | 'goals' | 'badges'

interface SimpleUser {
    id: number
    name: string
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatCurrency(value: number): string {
    return (value / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
}

function formatNumber(value: number): string {
    return value.toLocaleString('pt-BR')
}

function formatPercentage(value: number): string {
    return `${value.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`
}

function daysRemaining(endDate: string): number {
    const end = new Date(endDate)
    const now = new Date()
    const diff = end.getTime() - now.getTime()
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)))
}

function relativeTime(dateStr: string): string {
    const date = new Date(dateStr)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMin = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)

    if (diffMin < 1) return 'agora mesmo'
    if (diffMin < 60) return `${diffMin}min atrás`
    if (diffHours < 24) return `${diffHours}h atrás`
    if (diffDays < 7) return `${diffDays}d atrás`
    return date.toLocaleDateString('pt-BR')
}

function formatDate(dateStr: string): string {
    return new Date(dateStr).toLocaleDateString('pt-BR')
}

function getProgressColor(percentage: number): string {
    if (percentage >= 80) return 'text-success'
    if (percentage >= 50) return 'text-warning'
    return 'text-error'
}

function getProgressBarColor(percentage: number): string {
    if (percentage >= 80) return 'progress-success'
    if (percentage >= 50) return 'progress-warning'
    return 'progress-error'
}

function getRankChangeIcon(current: number, previous?: number | null) {
    if (!previous) return <MdRemove className="text-base-content/40" size={16} />
    if (current < previous) return <MdArrowUpward className="text-success" size={16} />
    if (current > previous) return <MdArrowDownward className="text-error" size={16} />
    return <MdRemove className="text-base-content/40" size={16} />
}

function getMedalColor(rank: number): string {
    if (rank === 1) return '#FFD700'
    if (rank === 2) return '#C0C0C0'
    if (rank === 3) return '#CD7F32'
    return ''
}

function getUserInitials(name?: string | null): string {
    if (!name) return 'U'
    const parts = name.trim().split(/\s+/)
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    return name.substring(0, 2).toUpperCase()
}

function getGoalValueDisplay(type: SalesGoalType, value: number): string {
    if (type === 'REVENUE') return formatCurrency(value)
    if (type === 'CONVERSION_RATE') return formatPercentage(value)
    if (type === 'AVG_CLOSE_TIME') return `${Math.round(value)} dias`
    return formatNumber(value)
}

// ── SVG Circular Progress ────────────────────────────────────────────────────

function CircularProgress({ percentage, size = 80, strokeWidth = 6 }: {
    percentage: number; size?: number; strokeWidth?: number
}) {
    const radius = (size - strokeWidth) / 2
    const circumference = 2 * Math.PI * radius
    const offset = circumference - (Math.min(percentage, 100) / 100) * circumference
    const color = percentage >= 80 ? '#22c55e' : percentage >= 50 ? '#eab308' : '#ef4444'

    return (
        <svg width={size} height={size} className="transform -rotate-90">
            <circle
                cx={size / 2}
                cy={size / 2}
                r={radius}
                fill="none"
                stroke="currentColor"
                strokeWidth={strokeWidth}
                className="text-base-300"
            />
            <circle
                cx={size / 2}
                cy={size / 2}
                r={radius}
                fill="none"
                stroke={color}
                strokeWidth={strokeWidth}
                strokeDasharray={circumference}
                strokeDashoffset={offset}
                strokeLinecap="round"
                className="transition-all duration-700 ease-out"
            />
            <text
                x={size / 2}
                y={size / 2}
                textAnchor="middle"
                dominantBaseline="central"
                className="fill-current text-base-content font-bold"
                fontSize={size * 0.2}
                transform={`rotate(90, ${size / 2}, ${size / 2})`}
            >
                {Math.round(percentage)}%
            </text>
        </svg>
    )
}

// ── Badge Icon Renderer ──────────────────────────────────────────────────────

function BadgeIcon({ icon, size = 24, className = '' }: { icon: string; size?: number; className?: string }) {
    // Map icon strings to react-icons or fallback to star
    return <MdMilitaryTech size={size} className={className} />
}

// ── TABS CONFIG ──────────────────────────────────────────────────────────────

const TABS: { key: Tab; label: string; icon: React.ReactNode }[] = [
    { key: 'profile', label: 'Meu Perfil', icon: <MdPerson size={18} /> },
    { key: 'rankings', label: 'Rankings', icon: <MdLeaderboard size={18} /> },
    { key: 'goals', label: 'Metas', icon: <MdFlag size={18} /> },
    { key: 'badges', label: 'Conquistas', icon: <MdMilitaryTech size={18} /> },
]

// ── BADGE CATEGORY LABELS ────────────────────────────────────────────────────

const BADGE_CATEGORY_LABELS: Record<string, string> = {
    volume: 'Volume',
    speed: 'Velocidade',
    quality: 'Qualidade',
    consistency: 'Consistência',
    rare: 'Raros',
}

// ── MAIN COMPONENT ──────────────────────────────────────────────────────────

export default function GamificationPage() {
    const auth = useAuth()
    const { canCreate } = useResourcePermissions('sales', 'gamification')

    // ── State ────────────────────────────────────────────────────────────────
    const [activeTab, setActiveTab] = useState<Tab>('profile')
    const [loading, setLoading] = useState(true)

    // Profile
    const [profile, setProfile] = useState<UserGamificationProfile | null>(null)
    const [pointHistory, setPointHistory] = useState<SalesPointLog[]>([])
    const [pointPage, setPointPage] = useState(1)
    const [hasMorePoints, setHasMorePoints] = useState(true)
    const [loadingPoints, setLoadingPoints] = useState(false)

    // Rankings
    const [rankingPeriod, setRankingPeriod] = useState<RankingPeriod>('MONTHLY')
    const [rankings, setRankings] = useState<SalesRanking[]>([])
    const [loadingRankings, setLoadingRankings] = useState(false)

    // Goals
    const [goals, setGoals] = useState<SalesGoal[]>([])
    const [goalFilter, setGoalFilter] = useState<SalesGoalStatus | ''>('')
    const [loadingGoals, setLoadingGoals] = useState(false)
    const [showGoalForm, setShowGoalForm] = useState(false)
    const [creatingGoal, setCreatingGoal] = useState(false)
    const [goalToCancel, setGoalToCancel] = useState<SalesGoal | null>(null)
    const [cancellingGoal, setCancellingGoal] = useState(false)

    // Goal form state
    const [goalForm, setGoalForm] = useState<CreateGoalData>({
        name: '',
        type: 'DEALS_WON',
        targetValue: 0,
        scope: 'INDIVIDUAL',
        period: 'MONTHLY',
        startDate: new Date().toISOString().split('T')[0],
        endDate: '',
        isRecurring: false,
    })

    // Users/Wallets for goal form
    const [users, setUsers] = useState<SimpleUser[]>([])
    const [wallets, setWallets] = useState<SalesWallet[]>([])
    const [loadingFormData, setLoadingFormData] = useState(false)

    // Badges
    const [badgesData, setBadgesData] = useState<BadgesResponse | null>(null)
    const [loadingBadges, setLoadingBadges] = useState(false)

    // ── Load Profile ─────────────────────────────────────────────────────────
    const loadProfile = useCallback(async () => {
        setLoading(true)
        const res = await getGamificationProfile()
        if (res.success) {
            setProfile(res.result)
        } else {
            toast.error(res.error || 'Erro ao carregar perfil de gamificação')
        }
        setLoading(false)
    }, [])

    // ── Load Point History ───────────────────────────────────────────────────
    const loadPointHistory = useCallback(async (page: number, append = false) => {
        setLoadingPoints(true)
        const res = await getPointHistory(page, 20)
        if (res.success) {
            const data = Array.isArray(res.result) ? res.result : []
            if (append) {
                setPointHistory(prev => [...prev, ...data])
            } else {
                setPointHistory(data)
            }
            setHasMorePoints(data.length === 20)
        }
        setLoadingPoints(false)
    }, [])

    // ── Load Rankings ────────────────────────────────────────────────────────
    const loadRankings = useCallback(async (period: RankingPeriod) => {
        setLoadingRankings(true)
        const res = await getRankings(period)
        if (res.success) {
            setRankings(Array.isArray(res.result) ? res.result : [])
        } else {
            toast.error(res.error || 'Erro ao carregar rankings')
        }
        setLoadingRankings(false)
    }, [])

    // ── Load Goals ───────────────────────────────────────────────────────────
    const loadGoals = useCallback(async (filters?: GoalFilters) => {
        setLoadingGoals(true)
        const res = await getGoals(filters)
        if (res.success) {
            setGoals(Array.isArray(res.result) ? res.result : [])
        } else {
            toast.error(res.error || 'Erro ao carregar metas')
        }
        setLoadingGoals(false)
    }, [])

    // ── Load Badges ──────────────────────────────────────────────────────────
    const loadBadges = useCallback(async () => {
        setLoadingBadges(true)
        const res = await getBadges()
        if (res.success) {
            setBadgesData(res.result)
        } else {
            toast.error(res.error || 'Erro ao carregar conquistas')
        }
        setLoadingBadges(false)
    }, [])

    // ── Load Form Data (users + wallets) ─────────────────────────────────────
    const loadFormData = useCallback(async () => {
        if (users.length > 0) return
        setLoadingFormData(true)
        const companyId = auth?.user?.companyId
        if (companyId) {
            const [usersRes, walletsRes] = await Promise.all([
                getUsersByCompany(companyId),
                getWallets(),
            ])
            if (usersRes.success) {
                const list = Array.isArray(usersRes.result)
                    ? usersRes.result.map(u => ({ id: u.id, name: u.name }))
                    : []
                setUsers(list)
            }
            if (walletsRes.success) {
                setWallets(Array.isArray(walletsRes.result) ? walletsRes.result : [])
            }
        }
        setLoadingFormData(false)
    }, [auth?.user?.companyId, users.length])

    // ── Initial Load ─────────────────────────────────────────────────────────
    useEffect(() => {
        loadProfile()
        loadPointHistory(1)
    }, [loadProfile, loadPointHistory])

    // ── Tab Change Effects ───────────────────────────────────────────────────
    useEffect(() => {
        if (activeTab === 'rankings' && rankings.length === 0) {
            loadRankings(rankingPeriod)
        }
    }, [activeTab, rankings.length, rankingPeriod, loadRankings])

    useEffect(() => {
        if (activeTab === 'goals' && goals.length === 0) {
            loadGoals(goalFilter ? { status: goalFilter as SalesGoalStatus } : undefined)
        }
    }, [activeTab, goals.length, goalFilter, loadGoals])

    useEffect(() => {
        if (activeTab === 'badges' && !badgesData) {
            loadBadges()
        }
    }, [activeTab, badgesData, loadBadges])

    // ── Handlers ─────────────────────────────────────────────────────────────
    const handleRankingPeriodChange = (period: RankingPeriod) => {
        setRankingPeriod(period)
        loadRankings(period)
    }

    const handleGoalFilterChange = (status: SalesGoalStatus | '') => {
        setGoalFilter(status)
        loadGoals(status ? { status: status as SalesGoalStatus } : undefined)
    }

    const handleLoadMorePoints = () => {
        const nextPage = pointPage + 1
        setPointPage(nextPage)
        loadPointHistory(nextPage, true)
    }

    const handleOpenGoalForm = () => {
        loadFormData()
        setGoalForm({
            name: '',
            type: 'DEALS_WON',
            targetValue: 0,
            scope: 'INDIVIDUAL',
            period: 'MONTHLY',
            startDate: new Date().toISOString().split('T')[0],
            endDate: '',
            isRecurring: false,
        })
        setShowGoalForm(true)
    }

    const handleCreateGoal = async () => {
        if (!goalForm.name.trim()) {
            toast.warning('Informe o nome da meta')
            return
        }
        if (!goalForm.targetValue || goalForm.targetValue <= 0) {
            toast.warning('Informe o valor alvo')
            return
        }
        if (!goalForm.endDate) {
            toast.warning('Informe a data de término')
            return
        }
        if (goalForm.scope === 'INDIVIDUAL' && !goalForm.userId) {
            toast.warning('Selecione o usuário')
            return
        }
        if (goalForm.scope === 'WALLET' && !goalForm.walletId) {
            toast.warning('Selecione a carteira')
            return
        }

        setCreatingGoal(true)
        const res = await createGoal(goalForm)
        if (res.success) {
            toast.success('Meta criada com sucesso')
            setShowGoalForm(false)
            loadGoals(goalFilter ? { status: goalFilter as SalesGoalStatus } : undefined)
        } else {
            toast.error(res.error || 'Erro ao criar meta')
        }
        setCreatingGoal(false)
    }

    const handleCancelGoal = async () => {
        if (!goalToCancel) return
        setCancellingGoal(true)
        const res = await cancelGoal(goalToCancel.id)
        if (res.success) {
            toast.success('Meta cancelada')
            setGoalToCancel(null)
            loadGoals(goalFilter ? { status: goalFilter as SalesGoalStatus } : undefined)
        } else {
            toast.error(res.error || 'Erro ao cancelar meta')
        }
        setCancellingGoal(false)
    }

    // ── Derived Data ─────────────────────────────────────────────────────────
    const activeGoals = useMemo(() => goals.filter(g => g.status === 'ACTIVE'), [goals])
    const historyGoals = useMemo(() => goals.filter(g => g.status !== 'ACTIVE'), [goals])

    const podium = useMemo(() => {
        if (rankings.length < 1) return { first: null, second: null, third: null }
        return {
            first: rankings.find(r => r.rank === 1) || null,
            second: rankings.find(r => r.rank === 2) || null,
            third: rankings.find(r => r.rank === 3) || null,
        }
    }, [rankings])

    const badgesByCategory = useMemo(() => {
        if (!badgesData) return {}
        const categories: Record<string, { definitions: SalesBadgeDefinition[]; earned: Set<number> }> = {}
        const earnedSet = new Set(badgesData.earned.map(e => e.badgeId))

        for (const def of badgesData.definitions) {
            const cat = def.category || 'other'
            if (!categories[cat]) {
                categories[cat] = { definitions: [], earned: earnedSet }
            }
            categories[cat].definitions.push(def)
        }
        return categories
    }, [badgesData])

    const totalBadgesEarned = badgesData?.earned?.length || 0
    const totalBadgesAvailable = badgesData?.definitions?.length || 0

    const currentUserId = auth?.user?.id

    // ── Render Loading ───────────────────────────────────────────────────────
    if (loading && !profile) {
        return (
            <div className="p-6">
                <div className="flex items-center gap-3 mb-6">
                    <MdEmojiEvents size={28} className="text-secondary" />
                    <h1 className="text-2xl font-bold">Gamificação</h1>
                </div>
                <div className="flex justify-center py-20">
                    <span className="loading loading-spinner loading-lg text-secondary" />
                </div>
            </div>
        )
    }

    // ── TAB 1: Meu Perfil ────────────────────────────────────────────────────
    const renderProfileTab = () => {
        if (!profile) return <div className="text-center py-10 text-base-content/50">Nenhum dado de perfil encontrado.</div>

        return (
            <div className="space-y-6">
                {/* Hero Card */}
                <div className="card bg-gradient-to-r from-secondary/10 to-secondary/10 shadow-lg">
                    <div className="card-body">
                        <div className="flex flex-col md:flex-row items-center gap-6">
                            {/* Avatar */}
                            <div className="w-20 h-20 rounded-full bg-secondary text-primary-content flex items-center justify-center text-2xl font-bold shadow-md">
                                {getUserInitials(profile.userName)}
                            </div>
                            {/* Info */}
                            <div className="flex-1 text-center md:text-left">
                                <h2 className="text-xl font-bold">{profile.userName}</h2>
                                <div className="flex flex-col md:flex-row items-center gap-4 mt-2">
                                    <div>
                                        <span className="text-4xl font-extrabold text-secondary">{formatNumber(profile.totalPoints)}</span>
                                        <span className="text-sm text-base-content/60 ml-2">pontos</span>
                                    </div>
                                    {profile.rank > 0 && (
                                        <div className="flex items-center gap-2">
                                            {profile.rank <= 3 ? (
                                                <MdEmojiEvents size={28} style={{ color: getMedalColor(profile.rank) }} />
                                            ) : (
                                                <MdLeaderboard size={24} className="text-base-content/50" />
                                            )}
                                            <span className="text-lg font-semibold">
                                                {profile.rank}a posição
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Recent Badges */}
                {profile.badges && profile.badges.length > 0 && (
                    <div>
                        <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                            <MdMilitaryTech size={20} className="text-warning" />
                            Conquistas Recentes
                        </h3>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                            {profile.badges.slice(0, 3).map(be => (
                                <div
                                    key={be.id}
                                    className="card card-compact bg-base-100 shadow border-2"
                                    style={{ borderColor: BADGE_RARITY_COLORS[be.badge.rarity] }}
                                >
                                    <div className="card-body flex-row items-center gap-3">
                                        <BadgeIcon icon={be.badge.icon} size={28} className="text-secondary" />
                                        <div className="flex-1 min-w-0">
                                            <p className="font-semibold text-sm truncate">{be.badge.name}</p>
                                            <span
                                                className="text-xs font-medium"
                                                style={{ color: BADGE_RARITY_COLORS[be.badge.rarity] }}
                                            >
                                                {BADGE_RARITY_LABELS[be.badge.rarity]}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Active Goals */}
                {profile.activeGoals && profile.activeGoals.length > 0 && (
                    <div>
                        <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                            <MdFlag size={20} className="text-info" />
                            Metas Ativas
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {profile.activeGoals.map(goal => {
                                const pct = goal.targetValue > 0
                                    ? Math.min(100, (goal.currentValue / goal.targetValue) * 100)
                                    : 0
                                const remaining = daysRemaining(goal.endDate)

                                return (
                                    <div key={goal.id} className="card bg-base-100 shadow">
                                        <div className="card-body">
                                            <div className="flex items-start gap-4">
                                                <CircularProgress percentage={pct} size={70} strokeWidth={5} />
                                                <div className="flex-1 min-w-0">
                                                    <h4 className="font-semibold truncate">{goal.name}</h4>
                                                    <p className="text-xs text-base-content/50 mb-1">
                                                        {GOAL_TYPE_LABELS[goal.type]}
                                                    </p>
                                                    <p className="text-sm font-medium">
                                                        <span className={getProgressColor(pct)}>
                                                            {getGoalValueDisplay(goal.type, goal.currentValue)}
                                                        </span>
                                                        <span className="text-base-content/50">
                                                            {' / '}{getGoalValueDisplay(goal.type, goal.targetValue)}
                                                        </span>
                                                    </p>
                                                    <p className="text-xs text-base-content/40 mt-1 flex items-center gap-1">
                                                        <MdAccessTime size={12} />
                                                        {remaining > 0 ? `${remaining} dias restantes` : 'Encerrada'}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    </div>
                )}

                {/* Point History */}
                <div>
                    <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                        <MdHistory size={20} className="text-success" />
                        Histórico de Pontos
                    </h3>
                    {pointHistory.length === 0 ? (
                        <p className="text-base-content/50 text-sm">Nenhum ponto registrado ainda.</p>
                    ) : (
                        <div className="space-y-2">
                            {pointHistory.map(pl => (
                                <div key={pl.id} className="flex items-center gap-3 p-3 bg-base-100 rounded-lg shadow-sm">
                                    <span className={`text-sm font-bold min-w-[60px] text-right ${pl.points > 0 ? 'text-success' : 'text-error'}`}>
                                        {pl.points > 0 ? '+' : ''}{pl.points}
                                    </span>
                                    <span className="flex-1 text-sm">{pl.reason}</span>
                                    <span className="text-xs text-base-content/40 whitespace-nowrap">
                                        {relativeTime(pl.createdAt)}
                                    </span>
                                </div>
                            ))}
                            {hasMorePoints && (
                                <button
                                    className="btn btn-ghost btn-sm w-full"
                                    onClick={handleLoadMorePoints}
                                    disabled={loadingPoints}
                                >
                                    {loadingPoints ? (
                                        <span className="loading loading-spinner loading-xs" />
                                    ) : (
                                        'Carregar mais'
                                    )}
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>
        )
    }

    // ── TAB 2: Rankings ──────────────────────────────────────────────────────
    const renderRankingsTab = () => {
        return (
            <div className="space-y-6">
                {/* Period Selector */}
                <div className="flex flex-wrap gap-2">
                    {(Object.keys(RANKING_PERIOD_LABELS) as RankingPeriod[]).map(period => (
                        <button
                            key={period}
                            className={`btn btn-sm ${rankingPeriod === period ? 'btn-secondary' : 'btn-ghost'}`}
                            onClick={() => handleRankingPeriodChange(period)}
                        >
                            {RANKING_PERIOD_LABELS[period]}
                        </button>
                    ))}
                </div>

                {loadingRankings ? (
                    <div className="flex justify-center py-16">
                        <span className="loading loading-spinner loading-lg text-secondary" />
                    </div>
                ) : rankings.length === 0 ? (
                    <div className="text-center py-16 text-base-content/50">
                        <MdLeaderboard size={48} className="mx-auto mb-3 opacity-30" />
                        <p>Nenhum ranking disponível para este período</p>
                    </div>
                ) : (
                    <>
                        {/* Podium */}
                        <div className="flex justify-center items-end gap-4 py-6">
                            {/* 2nd Place */}
                            {podium.second && (
                                <div className="flex flex-col items-center w-28">
                                    <div
                                        className="w-14 h-14 rounded-full flex items-center justify-center text-lg font-bold shadow-md mb-2"
                                        style={{ backgroundColor: '#C0C0C020', border: '3px solid #C0C0C0' }}
                                    >
                                        {getUserInitials(podium.second.user?.name || 'U')}
                                    </div>
                                    <MdEmojiEvents size={24} style={{ color: '#C0C0C0' }} />
                                    <p className="text-sm font-semibold mt-1 text-center truncate w-full">
                                        {podium.second.user?.name || 'Usuário'}
                                    </p>
                                    <p className="text-xs text-base-content/50">{formatNumber(podium.second.totalPoints)} pts</p>
                                    <p className="text-xs text-base-content/40">{podium.second.dealsWon} negócios</p>
                                    <p className="text-xs text-base-content/40">{formatCurrency(podium.second.totalRevenue)}</p>
                                    <div className="w-full h-20 bg-base-300/50 rounded-t-lg mt-2" />
                                </div>
                            )}
                            {/* 1st Place */}
                            {podium.first && (
                                <div className="flex flex-col items-center w-32">
                                    <div
                                        className="w-18 h-18 rounded-full flex items-center justify-center text-xl font-bold shadow-lg mb-2"
                                        style={{ backgroundColor: '#FFD70020', border: '3px solid #FFD700', width: 72, height: 72 }}
                                    >
                                        {getUserInitials(podium.first.user?.name || 'U')}
                                    </div>
                                    <MdEmojiEvents size={32} style={{ color: '#FFD700' }} />
                                    <p className="text-sm font-bold mt-1 text-center truncate w-full">
                                        {podium.first.user?.name || 'Usuário'}
                                    </p>
                                    <p className="text-sm font-semibold text-secondary">{formatNumber(podium.first.totalPoints)} pts</p>
                                    <p className="text-xs text-base-content/40">{podium.first.dealsWon} negócios</p>
                                    <p className="text-xs text-base-content/40">{formatCurrency(podium.first.totalRevenue)}</p>
                                    <div className="w-full h-28 bg-secondary/20 rounded-t-lg mt-2" />
                                </div>
                            )}
                            {/* 3rd Place */}
                            {podium.third && (
                                <div className="flex flex-col items-center w-28">
                                    <div
                                        className="w-14 h-14 rounded-full flex items-center justify-center text-lg font-bold shadow-md mb-2"
                                        style={{ backgroundColor: '#CD7F3220', border: '3px solid #CD7F32' }}
                                    >
                                        {getUserInitials(podium.third.user?.name || 'U')}
                                    </div>
                                    <MdEmojiEvents size={24} style={{ color: '#CD7F32' }} />
                                    <p className="text-sm font-semibold mt-1 text-center truncate w-full">
                                        {podium.third.user?.name || 'Usuário'}
                                    </p>
                                    <p className="text-xs text-base-content/50">{formatNumber(podium.third.totalPoints)} pts</p>
                                    <p className="text-xs text-base-content/40">{podium.third.dealsWon} negócios</p>
                                    <p className="text-xs text-base-content/40">{formatCurrency(podium.third.totalRevenue)}</p>
                                    <div className="w-full h-14 bg-base-300/30 rounded-t-lg mt-2" />
                                </div>
                            )}
                        </div>

                        {/* Full Ranking Table */}
                        <div className="overflow-x-auto">
                            <table className="table table-sm">
                                <thead>
                                    <tr>
                                        <th className="w-16">#</th>
                                        <th></th>
                                        <th>Nome</th>
                                        <th className="text-right">Pontos</th>
                                        <th className="text-right">Negócios Ganhos</th>
                                        <th className="text-right">Receita</th>
                                        <th className="text-right">Taxa de Conversão</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rankings.map(r => {
                                        const isCurrentUser = r.userId === currentUserId
                                        return (
                                            <tr
                                                key={r.id}
                                                className={isCurrentUser ? 'bg-secondary/10 font-semibold' : ''}
                                            >
                                                <td>
                                                    <div className="flex items-center gap-1">
                                                        {getRankChangeIcon(r.rank, r.previousRank)}
                                                        <span className={r.rank <= 3 ? 'font-bold' : ''}>
                                                            {r.rank <= 3 ? (
                                                                <MdEmojiEvents
                                                                    size={18}
                                                                    style={{ color: getMedalColor(r.rank) }}
                                                                />
                                                            ) : (
                                                                r.rank
                                                            )}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="w-8 h-8 rounded-full bg-base-300 flex items-center justify-center text-xs font-bold">
                                                        {getUserInitials(r.user?.name || 'U')}
                                                    </div>
                                                </td>
                                                <td>{r.user?.name || '-'}</td>
                                                <td className="text-right">{formatNumber(r.totalPoints)}</td>
                                                <td className="text-right">{r.dealsWon}</td>
                                                <td className="text-right">{formatCurrency(r.totalRevenue)}</td>
                                                <td className="text-right">{formatPercentage(r.conversionRate)}</td>
                                            </tr>
                                        )
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}
            </div>
        )
    }

    // ── TAB 3: Metas ─────────────────────────────────────────────────────────
    const renderGoalsTab = () => {
        return (
            <div className="space-y-6">
                {/* Header */}
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                    <div className="flex flex-wrap gap-2">
                        <button
                            className={`btn btn-sm ${goalFilter === '' ? 'btn-secondary' : 'btn-ghost'}`}
                            onClick={() => handleGoalFilterChange('')}
                        >
                            Todas
                        </button>
                        {(Object.keys(GOAL_STATUS_LABELS) as SalesGoalStatus[]).map(status => (
                            <button
                                key={status}
                                className={`btn btn-sm ${goalFilter === status ? 'btn-secondary' : 'btn-ghost'}`}
                                onClick={() => handleGoalFilterChange(status)}
                            >
                                {GOAL_STATUS_LABELS[status]}
                            </button>
                        ))}
                    </div>
                    {canCreate && (
                        <button className="btn btn-secondary btn-sm" onClick={handleOpenGoalForm}>
                            <MdAdd size={18} />
                            Nova Meta
                        </button>
                    )}
                </div>

                {loadingGoals ? (
                    <div className="flex justify-center py-16">
                        <span className="loading loading-spinner loading-lg text-secondary" />
                    </div>
                ) : (
                    <>
                        {/* Active Goals */}
                        {activeGoals.length > 0 && (
                            <div>
                                <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                                    <MdFlag size={20} className="text-info" />
                                    Metas Ativas
                                </h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {activeGoals.map(goal => {
                                        const pct = goal.targetValue > 0
                                            ? Math.min(100, (goal.currentValue / goal.targetValue) * 100)
                                            : 0
                                        const remaining = daysRemaining(goal.endDate)

                                        return (
                                            <div key={goal.id} className="card bg-base-100 shadow">
                                                <div className="card-body">
                                                    <div className="flex items-start justify-between">
                                                        <div className="flex-1 min-w-0">
                                                            <h4 className="font-semibold truncate">{goal.name}</h4>
                                                            <div className="flex flex-wrap gap-1 mt-1">
                                                                <span className="badge badge-sm badge-outline">
                                                                    {GOAL_TYPE_LABELS[goal.type]}
                                                                </span>
                                                                <span className="badge badge-sm badge-outline">
                                                                    {GOAL_SCOPE_LABELS[goal.scope]}
                                                                </span>
                                                                {goal.isRecurring && (
                                                                    <span className="badge badge-sm badge-accent badge-outline">
                                                                        Recorrente
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                        {canCreate && (
                                                            <button
                                                                className="btn btn-ghost btn-xs text-error"
                                                                onClick={() => setGoalToCancel(goal)}
                                                                title="Cancelar meta"
                                                            >
                                                                <MdCancel size={16} />
                                                            </button>
                                                        )}
                                                    </div>

                                                    <div className="mt-3">
                                                        <div className="flex items-center justify-between text-sm mb-1">
                                                            <span className={getProgressColor(pct)}>
                                                                {getGoalValueDisplay(goal.type, goal.currentValue)}
                                                            </span>
                                                            <span className="text-base-content/50">
                                                                {getGoalValueDisplay(goal.type, goal.targetValue)}
                                                            </span>
                                                        </div>
                                                        <progress
                                                            className={`progress ${getProgressBarColor(pct)} w-full`}
                                                            value={pct}
                                                            max="100"
                                                        />
                                                        <div className="flex items-center justify-between mt-2 text-xs text-base-content/40">
                                                            <span>
                                                                {formatDate(goal.startDate)} - {formatDate(goal.endDate)}
                                                            </span>
                                                            <span className="flex items-center gap-1">
                                                                <MdAccessTime size={12} />
                                                                {remaining > 0 ? `${remaining} dias restantes` : 'Encerrada'}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )
                                    })}
                                </div>
                            </div>
                        )}

                        {/* History */}
                        {historyGoals.length > 0 && (
                            <div>
                                <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                                    <MdHistory size={20} className="text-base-content/50" />
                                    Histórico
                                </h3>
                                <div className="overflow-x-auto">
                                    <table className="table table-sm">
                                        <thead>
                                            <tr>
                                                <th>Status</th>
                                                <th>Nome</th>
                                                <th>Tipo</th>
                                                <th className="text-right">Meta</th>
                                                <th className="text-right">Resultado</th>
                                                <th>Período</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {historyGoals.map(goal => (
                                                <tr key={goal.id}>
                                                    <td>
                                                        <span className={`badge badge-sm ${GOAL_STATUS_COLORS[goal.status]}`}>
                                                            {GOAL_STATUS_LABELS[goal.status]}
                                                        </span>
                                                    </td>
                                                    <td className="font-medium">{goal.name}</td>
                                                    <td className="text-sm">{GOAL_TYPE_LABELS[goal.type]}</td>
                                                    <td className="text-right text-sm">
                                                        {getGoalValueDisplay(goal.type, goal.targetValue)}
                                                    </td>
                                                    <td className="text-right text-sm">
                                                        {getGoalValueDisplay(goal.type, goal.currentValue)}
                                                    </td>
                                                    <td className="text-sm text-base-content/50">
                                                        {formatDate(goal.startDate)} - {formatDate(goal.endDate)}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        {activeGoals.length === 0 && historyGoals.length === 0 && (
                            <div className="text-center py-16 text-base-content/50">
                                <MdFlag size={48} className="mx-auto mb-3 opacity-30" />
                                <p>Nenhuma meta encontrada</p>
                            </div>
                        )}
                    </>
                )}

                {/* Create Goal Modal */}
                {showGoalForm && (
                    <div className="modal modal-open">
                        <div className="modal-box !max-w-lg">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-bold">Nova Meta</h3>
                                <button className="btn btn-ghost btn-sm btn-circle" onClick={() => setShowGoalForm(false)}>
                                    <MdClose size={18} />
                                </button>
                            </div>

                            <div className="space-y-4">
                                {/* Nome */}
                                <div className="form-control">
                                    <label className="label">
                                        <span className="label-text font-medium">Nome</span>
                                    </label>
                                    <input
                                        type="text"
                                        className="input input-bordered w-full"
                                        placeholder="Ex: Meta mensal de vendas"
                                        value={goalForm.name}
                                        onChange={e => setGoalForm(f => ({ ...f, name: e.target.value }))}
                                    />
                                </div>

                                {/* Descrição */}
                                <div className="form-control">
                                    <label className="label">
                                        <span className="label-text font-medium">Descrição</span>
                                    </label>
                                    <textarea
                                        className="textarea textarea-bordered w-full"
                                        placeholder="Descrição opcional"
                                        rows={2}
                                        value={goalForm.description || ''}
                                        onChange={e => setGoalForm(f => ({ ...f, description: e.target.value }))}
                                    />
                                </div>

                                {/* Tipo */}
                                <div className="form-control">
                                    <label className="label">
                                        <span className="label-text font-medium">Tipo</span>
                                    </label>
                                    <select
                                        className="select select-bordered w-full"
                                        value={goalForm.type}
                                        onChange={e => setGoalForm(f => ({ ...f, type: e.target.value as SalesGoalType }))}
                                    >
                                        {(Object.keys(GOAL_TYPE_LABELS) as SalesGoalType[]).map(t => (
                                            <option key={t} value={t}>{GOAL_TYPE_LABELS[t]}</option>
                                        ))}
                                    </select>
                                </div>

                                {/* Valor Alvo */}
                                <div className="form-control">
                                    <label className="label">
                                        <span className="label-text font-medium">
                                            Valor Alvo ({GOAL_TYPE_UNITS[goalForm.type]})
                                        </span>
                                    </label>
                                    <input
                                        type="number"
                                        className="input input-bordered w-full"
                                        placeholder="0"
                                        min={0}
                                        step={goalForm.type === 'REVENUE' ? 100 : 1}
                                        value={goalForm.targetValue || ''}
                                        onChange={e => setGoalForm(f => ({ ...f, targetValue: Number(e.target.value) }))}
                                    />
                                </div>

                                {/* Escopo */}
                                <div className="form-control">
                                    <label className="label">
                                        <span className="label-text font-medium">Escopo</span>
                                    </label>
                                    <select
                                        className="select select-bordered w-full"
                                        value={goalForm.scope}
                                        onChange={e => setGoalForm(f => ({
                                            ...f,
                                            scope: e.target.value as SalesGoalScope,
                                            userId: undefined,
                                            walletId: undefined,
                                        }))}
                                    >
                                        {(Object.keys(GOAL_SCOPE_LABELS) as SalesGoalScope[]).map(s => (
                                            <option key={s} value={s}>{GOAL_SCOPE_LABELS[s]}</option>
                                        ))}
                                    </select>
                                </div>

                                {/* User Select (if INDIVIDUAL) */}
                                {goalForm.scope === 'INDIVIDUAL' && (
                                    <div className="form-control">
                                        <label className="label">
                                            <span className="label-text font-medium">Usuário</span>
                                        </label>
                                        {loadingFormData ? (
                                            <span className="loading loading-spinner loading-sm" />
                                        ) : (
                                            <select
                                                className="select select-bordered w-full"
                                                value={goalForm.userId || ''}
                                                onChange={e => setGoalForm(f => ({ ...f, userId: Number(e.target.value) || undefined }))}
                                            >
                                                <option value="">Selecione o usuário</option>
                                                {users.map(u => (
                                                    <option key={u.id} value={u.id}>{u.name}</option>
                                                ))}
                                            </select>
                                        )}
                                    </div>
                                )}

                                {/* Wallet Select (if WALLET) */}
                                {goalForm.scope === 'WALLET' && (
                                    <div className="form-control">
                                        <label className="label">
                                            <span className="label-text font-medium">Carteira</span>
                                        </label>
                                        {loadingFormData ? (
                                            <span className="loading loading-spinner loading-sm" />
                                        ) : (
                                            <select
                                                className="select select-bordered w-full"
                                                value={goalForm.walletId || ''}
                                                onChange={e => setGoalForm(f => ({ ...f, walletId: Number(e.target.value) || undefined }))}
                                            >
                                                <option value="">Selecione a carteira</option>
                                                {wallets.map(w => (
                                                    <option key={w.id} value={w.id}>{w.name}</option>
                                                ))}
                                            </select>
                                        )}
                                    </div>
                                )}

                                {/* Período */}
                                <div className="form-control">
                                    <label className="label">
                                        <span className="label-text font-medium">Período</span>
                                    </label>
                                    <select
                                        className="select select-bordered w-full"
                                        value={goalForm.period}
                                        onChange={e => setGoalForm(f => ({ ...f, period: e.target.value as SalesGoalPeriod }))}
                                    >
                                        {(Object.keys(GOAL_PERIOD_LABELS) as SalesGoalPeriod[]).map(p => (
                                            <option key={p} value={p}>{GOAL_PERIOD_LABELS[p]}</option>
                                        ))}
                                    </select>
                                </div>

                                {/* Datas */}
                                <div className="grid grid-cols-2 gap-3">
                                    <div className="form-control">
                                        <label className="label">
                                            <span className="label-text font-medium">Início</span>
                                        </label>
                                        <input
                                            type="date"
                                            className="input input-bordered w-full"
                                            value={goalForm.startDate}
                                            onChange={e => setGoalForm(f => ({ ...f, startDate: e.target.value }))}
                                        />
                                    </div>
                                    <div className="form-control">
                                        <label className="label">
                                            <span className="label-text font-medium">Término</span>
                                        </label>
                                        <input
                                            type="date"
                                            className="input input-bordered w-full"
                                            value={goalForm.endDate}
                                            onChange={e => setGoalForm(f => ({ ...f, endDate: e.target.value }))}
                                        />
                                    </div>
                                </div>

                                {/* Recorrente */}
                                <div className="form-control">
                                    <label className="label cursor-pointer justify-start gap-3">
                                        <input
                                            type="checkbox"
                                            className="toggle toggle-secondary toggle-sm"
                                            checked={goalForm.isRecurring || false}
                                            onChange={e => setGoalForm(f => ({ ...f, isRecurring: e.target.checked }))}
                                        />
                                        <span className="label-text font-medium">Meta Recorrente</span>
                                    </label>
                                </div>
                            </div>

                            <div className="modal-action">
                                <button className="btn btn-ghost" onClick={() => setShowGoalForm(false)}>
                                    Cancelar
                                </button>
                                <button
                                    className="btn btn-secondary"
                                    onClick={handleCreateGoal}
                                    disabled={creatingGoal}
                                >
                                    {creatingGoal ? (
                                        <span className="loading loading-spinner loading-sm" />
                                    ) : (
                                        'Criar Meta'
                                    )}
                                </button>
                            </div>
                        </div>
                        <div className="modal-backdrop" onClick={() => setShowGoalForm(false)} />
                    </div>
                )}

                {/* Cancel Goal Confirmation */}
                <ConfirmModal
                    isOpen={!!goalToCancel}
                    onClose={() => setGoalToCancel(null)}
                    onConfirm={handleCancelGoal}
                    title="Cancelar Meta"
                    message={`Tem certeza que deseja cancelar a meta "${goalToCancel?.name}"? Esta ação não pode ser desfeita.`}
                    loading={cancellingGoal}
                    confirmButtonText="Cancelar Meta"
                    confirmButtonClass="btn-error"
                />
            </div>
        )
    }

    // ── TAB 4: Conquistas ────────────────────────────────────────────────────
    const renderBadgesTab = () => {
        if (loadingBadges) {
            return (
                <div className="flex justify-center py-16">
                    <span className="loading loading-spinner loading-lg text-secondary" />
                </div>
            )
        }

        if (!badgesData) {
            return (
                <div className="text-center py-16 text-base-content/50">
                    <MdMilitaryTech size={48} className="mx-auto mb-3 opacity-30" />
                    <p>Nenhuma conquista disponível</p>
                </div>
            )
        }

        const earnedBadgeIds = new Set(badgesData.earned.map(e => e.badgeId))
        const earnedMap = new Map(badgesData.earned.map(e => [e.badgeId, e]))

        return (
            <div className="space-y-6">
                {/* Total Counter */}
                <div className="flex items-center gap-3">
                    <MdMilitaryTech size={24} className="text-warning" />
                    <span className="text-lg font-semibold">
                        {totalBadgesEarned} / {totalBadgesAvailable} conquistados
                    </span>
                </div>

                {/* Categories */}
                {Object.entries(badgesByCategory).map(([category, data]) => {
                    const categoryEarnedCount = data.definitions.filter(d => earnedBadgeIds.has(d.id)).length
                    const categoryLabel = BADGE_CATEGORY_LABELS[category] || category

                    return (
                        <div key={category}>
                            <div className="flex items-center justify-between mb-3">
                                <h3 className="text-lg font-semibold">{categoryLabel}</h3>
                                <span className="text-sm text-base-content/50">
                                    {categoryEarnedCount}/{data.definitions.length} conquistados
                                </span>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                                {data.definitions.map(def => {
                                    const isEarned = earnedBadgeIds.has(def.id)
                                    const earned = earnedMap.get(def.id)

                                    if (isEarned) {
                                        return (
                                            <div
                                                key={def.id}
                                                className="card card-compact bg-base-100 shadow border-2 hover:shadow-md transition-shadow"
                                                style={{ borderColor: BADGE_RARITY_COLORS[def.rarity] }}
                                            >
                                                <div className="card-body">
                                                    <div className="flex items-start gap-3">
                                                        <div
                                                            className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                                                            style={{ backgroundColor: BADGE_RARITY_COLORS[def.rarity] + '20' }}
                                                        >
                                                            <BadgeIcon
                                                                icon={def.icon}
                                                                size={22}
                                                                className="text-base-content"
                                                            />
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <p className="font-semibold text-sm truncate">{def.name}</p>
                                                            <p className="text-xs text-base-content/50 line-clamp-2 mt-0.5">
                                                                {def.description}
                                                            </p>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center justify-between mt-2">
                                                        <span
                                                            className="text-xs font-semibold"
                                                            style={{ color: BADGE_RARITY_COLORS[def.rarity] }}
                                                        >
                                                            {BADGE_RARITY_LABELS[def.rarity]}
                                                        </span>
                                                        {earned && (
                                                            <span className="text-xs text-base-content/40">
                                                                {formatDate(earned.earnedAt)}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        )
                                    }

                                    // Locked badge
                                    return (
                                        <div
                                            key={def.id}
                                            className="card card-compact bg-base-200/50 shadow border-2 border-dashed border-base-300 opacity-60"
                                        >
                                            <div className="card-body">
                                                <div className="flex items-start gap-3">
                                                    <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 bg-base-300/50">
                                                        <MdLock size={22} className="text-base-content/30" />
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <p className="font-semibold text-sm truncate text-base-content/60">
                                                            {def.name}
                                                        </p>
                                                        <p className="text-xs text-base-content/40 line-clamp-2 mt-0.5">
                                                            {def.description}
                                                        </p>
                                                    </div>
                                                </div>
                                                <div className="mt-2">
                                                    <span className="text-xs text-base-content/40">
                                                        {BADGE_RARITY_LABELS[def.rarity]}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>
                        </div>
                    )
                })}
            </div>
        )
    }

    // ── Main Render ──────────────────────────────────────────────────────────
    return (
        <div className="p-6">
            {/* Header */}
            <div className="flex items-center gap-3 mb-6">
                <MdEmojiEvents size={28} className="text-secondary" />
                <h1 className="text-2xl font-bold">Gamificação</h1>
            </div>

            {/* Tabs */}
            <div className="tabs tabs-boxed mb-6 bg-base-200 p-1">
                {TABS.map(tab => (
                    <button
                        key={tab.key}
                        className={`tab gap-2 ${activeTab === tab.key ? 'tab-active' : ''}`}
                        onClick={() => setActiveTab(tab.key)}
                    >
                        {tab.icon}
                        <span className="hidden sm:inline">{tab.label}</span>
                    </button>
                ))}
            </div>

            {/* Tab Content */}
            {activeTab === 'profile' && renderProfileTab()}
            {activeTab === 'rankings' && renderRankingsTab()}
            {activeTab === 'goals' && renderGoalsTab()}
            {activeTab === 'badges' && renderBadgesTab()}
        </div>
    )
}
